import React from 'react';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import useTestUploadData from './useTestUploadData';
import VideoUploadCard from './VideoUploadCard';

export default function TestUpload() {
  const {
    loading,
    error,
    applicationData,
    existingSubmissions,
    videos,
    allVideosUploaded,
    handleUploadTypeChange,
    handleFileChange,
    handleUrlChange,
    handleTitleChange,
    handleNotesChange,
    uploadVideo,
    resubmitVideo
  } = useTestUploadData();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Validating your upload link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="mailto:contact@torek.io"
            className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload Your Test Videos</h1>
          <p className="text-gray-600 mb-4">
            Hi {applicationData?.first_name}! Please upload your 2 test videos below. You can either upload video files or provide links to videos hosted elsewhere.
          </p>
          {allVideosUploaded && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
              <CheckCircle2 className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-900 font-semibold">All videos uploaded!</p>
                <p className="text-green-700 text-sm mt-1">
                  Thank you for submitting your test videos. Our team will review them and get back to you soon.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Video Upload Sections */}
        {videos.map((video, idx) => (
          <VideoUploadCard
            key={idx}
            video={video}
            videoIdx={idx}
            existingSubmissions={existingSubmissions}
            onUploadTypeChange={handleUploadTypeChange}
            onFileChange={handleFileChange}
            onUrlChange={handleUrlChange}
            onTitleChange={handleTitleChange}
            onNotesChange={handleNotesChange}
            onUpload={uploadVideo}
            onResubmit={resubmitVideo}
          />
        ))}

        {/* Support Contact */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-900 mb-2">
            Having trouble uploading? Need to resubmit a video?
          </p>
          <a
            href="mailto:contact@torek.io"
            className="text-blue-600 hover:text-blue-800 font-semibold"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
